import { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import {
  formatCurrencyView,
  formatCurrencyInput,
  handleCurrencyChange,
  unformatCurrency,
} from '../../utils/currencyUtils';

function RendimentoModal({ show, onClose, rendimento, onSaveSuccess }) {
  const isEdit = Boolean(rendimento);
  const [form, setForm] = useState({
    cnpjAssociacao: localStorage.getItem('cnpj') || '',
    ano: localStorage.getItem('anoBase') || new Date().getFullYear().toString(),
    numConta: '',
    idPrograma: '',
    janeiro: 'R$ 0,00',
    fevereiro: 'R$ 0,00',
    marco: 'R$ 0,00',
    abril: 'R$ 0,00',
    maio: 'R$ 0,00',
    junho: 'R$ 0,00',
    julho: 'R$ 0,00',
    agosto: 'R$ 0,00',
    setembro: 'R$ 0,00',
    outubro: 'R$ 0,00',
    novembro: 'R$ 0,00',
    dezembro: 'R$ 0,00',
    totalRendimentos: 'R$ 0,00',
  });
  const [contas, setContas] = useState([]);
  const [programas, setProgramas] = useState([]);
  const [modalData, setModalData] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [isAutoEdit, setIsAutoEdit] = useState(false);

  useEffect(() => {
    // Carregar contas ao abrir o modal
    if (show) {
      axios
        .get(`${API_BASE_URL.CONTA}/all`, {
          params: { cnpjAssociacao: localStorage.getItem('cnpj') },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        })
        .then(res => setContas(Array.isArray(res.data) ? res.data : []))
        .catch(() => setContas([]));
    }
    if (isEdit && rendimento) {
      const formattedData = {
        ...rendimento,
        janeiro: formatCurrencyView(rendimento.janeiro),
        fevereiro: formatCurrencyView(rendimento.fevereiro),
        marco: formatCurrencyView(rendimento.marco),
        abril: formatCurrencyView(rendimento.abril),
        maio: formatCurrencyView(rendimento.maio),
        junho: formatCurrencyView(rendimento.junho),
        julho: formatCurrencyView(rendimento.julho),
        agosto: formatCurrencyView(rendimento.agosto),
        setembro: formatCurrencyView(rendimento.setembro),
        outubro: formatCurrencyView(rendimento.outubro),
        novembro: formatCurrencyView(rendimento.novembro),
        dezembro: formatCurrencyView(rendimento.dezembro),
      };
      // Calcular total de rendimentos baseado nos dados carregados
      formattedData.totalRendimentos = calculateTotalRendimentos(formattedData);
      setForm(formattedData);
    } else {
      setForm({
        cnpjAssociacao: localStorage.getItem('cnpj') || '',
        ano:
          localStorage.getItem('anoBase') ||
          new Date().getFullYear().toString(),
        numConta: '',
        idPrograma: '',
        janeiro: 'R$ 0,00',
        fevereiro: 'R$ 0,00',
        marco: 'R$ 0,00',
        abril: 'R$ 0,00',
        maio: 'R$ 0,00',
        junho: 'R$ 0,00',
        julho: 'R$ 0,00',
        agosto: 'R$ 0,00',
        setembro: 'R$ 0,00',
        outubro: 'R$ 0,00',
        novembro: 'R$ 0,00',
        dezembro: 'R$ 0,00',
        totalRendimentos: 'R$ 0,00',
      });
      setIsAutoEdit(false);
    }
  }, [show, isEdit, rendimento]);

  useEffect(() => {
    if (form.numConta) {
      axios
        .get(`${API_BASE_URL.CONTA_PROGRAMA}/${encodeURIComponent(form.cnpjAssociacao)}/${encodeURIComponent(form.numConta)}/atual`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        })
        .then(res => {
          setProgramas(res.status === 200 && Array.isArray(res.data) ? res.data : []);
        })
        .catch(() => setProgramas([]));
    } else {
      setProgramas([]);
    }
  }, [form.numConta, form.cnpjAssociacao]);

  useEffect(() => {
    if (isEdit || !form.numConta || !form.idPrograma) {
      return;
    }
    const cnpj = form.cnpjAssociacao;
    const ano = form.ano;
    const numConta = form.numConta;
    const idPrograma = form.idPrograma;
    axios
      .get(`${API_BASE_URL.RENDIMENTO}/${encodeURIComponent(cnpj)}/${ano}/${encodeURIComponent(numConta)}/${idPrograma}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then(res => {
        if (res.data) {
          const s = res.data;
          setForm(prev => {
            const updated = {
              ...prev,
              janeiro: formatCurrencyView(s.janeiro),
              fevereiro: formatCurrencyView(s.fevereiro),
              marco: formatCurrencyView(s.marco),
              abril: formatCurrencyView(s.abril),
              maio: formatCurrencyView(s.maio),
              junho: formatCurrencyView(s.junho),
              julho: formatCurrencyView(s.julho),
              agosto: formatCurrencyView(s.agosto),
              setembro: formatCurrencyView(s.setembro),
              outubro: formatCurrencyView(s.outubro),
              novembro: formatCurrencyView(s.novembro),
              dezembro: formatCurrencyView(s.dezembro),
            };
            updated.totalRendimentos = calculateTotalRendimentos(updated);
            return updated;
          });
          setIsAutoEdit(true);
        }
      })
      .catch(() => {
        setIsAutoEdit(false);
      });
  }, [form.numConta, form.idPrograma, form.cnpjAssociacao, form.ano, isEdit]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'numConta' ? { idPrograma: '' } : {}),
    }));
  };

  const calculateTotalRendimentos = updatedForm => {
    const meses = [
      'janeiro',
      'fevereiro',
      'marco',
      'abril',
      'maio',
      'junho',
      'julho',
      'agosto',
      'setembro',
      'outubro',
      'novembro',
      'dezembro',
    ];

    const somaMeses = meses.reduce((total, mes) => {
      const valor = unformatCurrency(updatedForm[mes] || 'R$ 0,00');
      return total + (isNaN(valor) ? 0 : valor);
    }, 0);

    return formatCurrencyView(somaMeses);
  };

  const CURRENCY_ALLOWED_KEYS = new Set([
    'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End',
  ]);

  const handleCurrencyKeyDown = e => {
    if (e.ctrlKey || e.metaKey) return;
    if (CURRENCY_ALLOWED_KEYS.has(e.key)) return;
    if (/^[\d\-,.]$/.test(e.key)) return;
    e.preventDefault();
  };

  const handleCurrencyFieldChange = e => {
    const { name, value } = e.target;
    const formattedValue = formatCurrencyInput(value);

    setForm(prev => {
      const updatedForm = {
        ...prev,
        [name]: formattedValue,
      };

      // Se alterou algum mês, recalcula o total de rendimentos
      if (
        [
          'janeiro',
          'fevereiro',
          'marco',
          'abril',
          'maio',
          'junho',
          'julho',
          'agosto',
          'setembro',
          'outubro',
          'novembro',
          'dezembro',
        ].includes(name)
      ) {
        updatedForm.totalRendimentos = calculateTotalRendimentos(updatedForm);
      }

      return updatedForm;
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      // Preparar dados para envio - desformatar valores de moeda
      const dataToSend = {
        ...form,
        idPrograma: form.idPrograma ? parseInt(form.idPrograma, 10) : null,
        janeiro: unformatCurrency(form.janeiro),
        fevereiro: unformatCurrency(form.fevereiro),
        marco: unformatCurrency(form.marco),
        abril: unformatCurrency(form.abril),
        maio: unformatCurrency(form.maio),
        junho: unformatCurrency(form.junho),
        julho: unformatCurrency(form.julho),
        agosto: unformatCurrency(form.agosto),
        setembro: unformatCurrency(form.setembro),
        outubro: unformatCurrency(form.outubro),
        novembro: unformatCurrency(form.novembro),
        dezembro: unformatCurrency(form.dezembro),
      };

      console.log('Dados enviados:', dataToSend);

      // if (isEdit) {
      //   await axios.put(`${API_BASE_URL.RENDIMENTO}/update`, dataToSend, {
      //     headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      //   });
      //   setModalData({
      //     show: true,
      //     type: 'success',
      //     message: 'Rendimento atualizado com sucesso!',
      //   });
      // } else {
      //   await axios.post(`${API_BASE_URL.RENDIMENTO}/create`, dataToSend, {
      //     headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      //   });
      //   setModalData({
      //     show: true,
      //     type: 'success',
      //     message: 'Rendimento criado com sucesso!',
      //   });
      // }
      const isExisting = isEdit || isAutoEdit;
      const url = isExisting
        ? `${API_BASE_URL.RENDIMENTO}/update`
        : `${API_BASE_URL.RENDIMENTO}/create`;

      await axios({
        method: isExisting ? 'put' : 'post',
        url,
        data: dataToSend,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setModalData({
        show: true,
        type: 'success',
        message: isExisting
          ? 'Rendimento atualizado com sucesso!'
          : 'Rendimento criado com sucesso!',
      });
      if (onSaveSuccess) onSaveSuccess();
    } catch {
      setModalData({
        show: true,
        type: 'error',
        message: 'Erro ao salvar rendimento.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
      <Modal.Header
        closeButton
        closeVariant="white"
        className={isEdit || isAutoEdit ? 'bg-primary text-white' : 'bg-success text-white'}
        style={{ height: '30px' }}
      >
        <Modal.Title>
          {isEdit ? 'EDITAR RENDIMENTO' : isAutoEdit ? 'EDITAR RENDIMENTO (EXISTENTE)' : 'NOVO RENDIMENTO'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} className="small">
          <div className="row g-2 mb-2">
            <div className="col-5">
              <label className="form-label">Conta</label>
              <select
                name="numConta"
                className="form-control form-control-sm"
                value={form.numConta}
                onChange={handleChange}
                disabled={isEdit}
                required
              >
                <option value="">Selecione...</option>
                {contas.map(conta => (
                  <option key={conta.numConta} value={conta.numConta}>
                    {conta.numConta}{' '}
                    {conta.idBanco
                      ? `- ${conta.idBanco} ${conta.nomeBanco} ${
                          conta.descricao
                            ? String(conta.descricao).toUpperCase()
                            : ''
                        }`
                      : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-5">
              <label className="form-label">Programa</label>
              <select
                name="idPrograma"
                className="form-control form-control-sm"
                value={form.idPrograma}
                onChange={handleChange}
              >
                <option value="">Selecione...</option>
                {programas.map(p => (
                  <option key={p.idPrograma} value={p.idPrograma}>
                    {p.nomePrograma}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-2">
              <label className="form-label">Total Rendimentos</label>
              <input
                name="totalRendimentos"
                type="text"
                className="form-control form-control-sm text-end bg-light fw-bold"
                value={form.totalRendimentos || 'R$ 0,00'}
                readOnly
                title="Campo calculado automaticamente: Soma de todos os meses"
              />
            </div>
          </div>
          <div className="row g-2 mb-2">
            {[
              'janeiro',
              'fevereiro',
              'marco',
              'abril',
              'maio',
              'junho',
              'julho',
              'agosto',
              'setembro',
              'outubro',
              'novembro',
              'dezembro',
            ].map(mes => (
              <div className="col-2" key={mes}>
                <label className="form-label text-capitalize">{mes}</label>
                <input
                  name={mes}
                  type="text"
                  className={`form-control form-control-sm text-end ${
                    unformatCurrency(form[mes] || '0') < 0 ? 'text-danger fw-bold' : ''
                  }`}
                  value={form[mes] || 'R$ 0,00'}
                  onChange={handleCurrencyFieldChange}
                  onKeyDown={handleCurrencyKeyDown}
                  onFocus={e => e.target.select()}
                  inputMode="decimal"
                />
              </div>
            ))}
          </div>
          <div className="d-flex justify-content-end mt-2">
            <button
              type="button"
              className="btn btn-secondary btn-sm me-2"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={loading}
            >
              {isEdit || isAutoEdit ? 'Atualizar' : 'Salvar'}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}

export default RendimentoModal;
